import { useState } from 'preact/hooks'
import { ExternalLink, Copy, Check, Pencil, Sparkles, Globe } from 'lucide-preact'
import type { LandingPageViewDto } from '@/types/landingPage'
import { ExternalUrlModal } from '@/components/webs/modals/ExternalUrlModal'
import { SwitchToBuilderModal } from '@/components/webs/modals/SwitchToBuilderModal'
import { useToast } from '@/hooks/useToast'

interface Props {
    landingPage: LandingPageViewDto
    canEdit: boolean
    onEditUrl: (url: string) => Promise<void>
    onSwitchToBuilder: () => Promise<void>
}

export function ExternalLandingPageCard({ landingPage, canEdit, onEditUrl, onSwitchToBuilder }: Props) {
    const [copied, setCopied] = useState(false)
    const [editOpen, setEditOpen] = useState(false)
    const [switchOpen, setSwitchOpen] = useState(false)
    const { showError } = useToast()

    const url = landingPage.DeployedUrl || ''
    const hasPriorDeployment = (landingPage.Deployments || []).some(d => d.Status === 'Live' || d.Status === 'Superseded')

    const copyUrl = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (e) {
            showError('Copy Failed', e instanceof Error ? e.message : 'Could not access clipboard')
        }
    }

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-6 space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                    <Globe className="w-3.5 h-3.5" />
                    External URL
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                    Bypass mode — page is hosted outside Orbital.
                </span>
            </div>

            {url ? (
                <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Public URL</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 max-w-full truncate"
                            title={url}
                        >
                            <ExternalLink className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                            <span className="truncate">{url}</span>
                        </a>
                        {/* Fixed width: "Copied" is narrower than "Copy URL", and on a wrapped row that
                            width change is enough to pull the button up beside the URL for the two seconds
                            the confirmation shows, then drop it back. */}
                        <button
                            type="button"
                            onClick={() => copyUrl(url)}
                            className="inline-flex items-center justify-center gap-1.5 min-w-[6.25rem] px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                            title="Copy URL"
                        >
                            {copied
                                ? <><Check className="w-3.5 h-3.5 text-green-500" /> Copied</>
                                : <><Copy className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" /> Copy URL</>}
                        </button>
                    </div>
                </div>
            ) : (
                <p className="text-sm italic text-slate-500 dark:text-slate-400">No URL set yet.</p>
            )}

            {canEdit && (
                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-200 dark:border-slate-700">
                    <button
                        type="button"
                        onClick={() => setEditOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                        <Pencil className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                        {url ? 'Edit URL' : 'Set URL'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setSwitchOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                        Switch to Builder
                    </button>
                </div>
            )}

            <ExternalUrlModal
                isOpen={editOpen}
                onClose={() => setEditOpen(false)}
                onSubmit={async (next) => {
                    await onEditUrl(next)
                    setEditOpen(false)
                }}
                title={url ? 'Edit external URL' : 'Set external URL'}
                description="Provide the public URL where your landing page is hosted. This becomes the URL that all campaign assets and emails will link to."
                submitLabel="Save URL"
                initialUrl={url}
            />
            <SwitchToBuilderModal
                isOpen={switchOpen}
                onClose={() => setSwitchOpen(false)}
                onConfirm={async () => {
                    await onSwitchToBuilder()
                    setSwitchOpen(false)
                }}
                hasPriorDeployment={hasPriorDeployment}
            />
        </div>
    )
}
